import { IVehicle } from "../../../models/Vehicle";
import useVehiclePackageData from "../../../data/useVehiclePackageData";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingWheel from "../../shared/LoadingWheel";
import PriceFormat from "../../../utils/priceFormat";
import { VehiclePackage } from "../../../models/VehiclePackageData";
import { useQueryClient } from "@tanstack/react-query";


export default function PartVehicleDetails({
  vehicle
}: {
  vehicle: IVehicle;
}) {
  const { t } = useTranslation("vehicles");
  const { vehiclePackageData, getVehiclePackageData } = useVehiclePackageData(vehicle.id, "PartVehicleSalvage");
  const [hasSalvage, setHasSalvage] = useState<boolean | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  // The value the fetch returned, shown straight away rather than waiting on whichever query feeds `vehicle`.
  const [fetchedValue, setFetchedValue] = useState<number | null | undefined>(undefined);
  // Each fetch is a paid UKVD lookup; a ref blocks a second click before the spinner has rendered.
  const fetchInFlight = useRef(false);
  const queryClient = useQueryClient();
  const salvageValue = fetchedValue !== undefined ? fetchedValue : vehicle?.valuationTradePoor;

  useEffect(() => {
    if (vehiclePackageData.isLoading || !vehiclePackageData.data) return;

    if(salvageValue != null) {
      setHasSalvage(true);
      return;
    }

    if (vehiclePackageData.data.data == null){
      setHasSalvage(false);
      return;
    }

    setHasSalvage(vehiclePackageData.data.data?.valuationDetailsFetchedAt !== null);
  }, [vehiclePackageData.isLoading, vehiclePackageData.data, salvageValue]);

  const handleFetchSalvageValue = () => {
    if (fetchInFlight.current) return;
    fetchInFlight.current = true;
    setIsFetching(true);

    getVehiclePackageData.mutate({ vehicleId: vehicle.id, package: VehiclePackage.JustSalvage },
      {
        // Also returned when the valuation was already stored, so a page that was behind the server shows it.
        onSuccess: (res) => {
          setFetchedValue(res.data?.valuationTradePoor ?? 0);
          setHasSalvage(true);
        },
        onSettled: () => {
          fetchInFlight.current = false;
          setIsFetching(false);
          queryClient.invalidateQueries(["lead"]);
          queryClient.invalidateQueries(["vehicles", vehicle.id]);
          queryClient.invalidateQueries(["vehiclePackage", vehicle.id]);
        }
      }
    );
  };

  if (hasSalvage === null) return <></>;

  if (hasSalvage) {
    return (<>
      <div className="py-2 grid grid-cols-3 gap-4">
        <dt className="text-sm font-medium text-gray-500">{t("Salvage Value")}</dt>
        <dd className={`text-sm col-span-2`}>
          {/* UKVD can return a valuation with no Trade Poor figure, which is stored as 0 */}
          {salvageValue ? PriceFormat(salvageValue) : t("Unavailable")}
        </dd>
      </div>
    </>);
  }

  return (
    <>
      <div className="py-2 grid grid-cols-3 gap-4">
        <dt className="text-sm font-medium text-gray-500">{t("Salvage Value")}</dt>
        <dd className={`text-sm col-span-2`}>
          {isFetching ? (
            <LoadingWheel className="w-5 h-5" wrapperClassName="" />
          ) : (
            <ButtonInput isSubmit={false} onClick={() => handleFetchSalvageValue()} label={t("fetch")} classes={"!py-1"} />
          )}
        </dd>
      </div>
    </>
  )
}
